<template>
  <div
    class="offcanvas offcanvas-fullscreen-sm-down offcanvas-end form-offcanvas"
    id="OffcanvasForm"
    ref="offcanvas"
    data-bs-backdrop="static"
  >
    <div class="offcanvas-header border-bottom bg-white py-3">
      <h6 class="offcanvas-title fw-bold mb-0 d-flex align-items-center gap-2">
        <i class="fa-solid fa-industry text-theme" />
        {{ type === 'CREATE'? $t('Create Batch'): $t('Edit Batch')  }}
      </h6>
      <button
        type="button"
        style="font-size: small;"
        class="btn-close shadow-none"
        data-bs-dismiss="offcanvas"
        aria-label="Close"
      />
    </div>

    <div
      class="offcanvas-body"
      v-if="loading"
    >
      <placeholder-content :is-load="true" />
    </div>
    <div
      class="offcanvas-body"
      v-else-if="!hasPermission"
    >
      <access-denied/>
    </div>
    <div
      class="offcanvas-body bg-body-secondary p-3"
      v-else
    >
      <div class="form-card px-3 py-3 mb-3">
        <div class="d-none d-md-block position-relative mt-4 px-4">
          <div class="progress" style="height: 4px; position: absolute; top: 20px; left: 0; width: 100%; z-index: 0;">
            <div class="progress-bar bg-secondary opacity-25" style="width: 100%"></div>
          </div>
          <div class="progress" style="height: 4px; position: absolute; top: 20px; left: 0; width: 100%; z-index: 1; background: transparent;">
            <div class="progress-bar bg-theme" :style="{ width: progressPercentage + '%' }"></div>
          </div>
          <div class="d-flex justify-content-between position-relative" style="z-index: 2;">
            <div
              v-for="(step, index) in steps"
              :key="step.id"
              class="text-center step-item"
              :class="{ 'active': step.id <= currentStep }"
            >
              <div
                class="step-circle mb-2 d-flex align-items-center justify-content-center mx-auto"
                :class="step.id <= currentStep ? 'bg-theme text-white shadow' : 'bg-white border text-secondary'"
              >
                <i v-if="step.id < currentStep" class="fa-solid fa-check"/>
                <span v-else>{{ step.id }}</span>
              </div>
              <div class="step-title" :class="step.id <= currentStep ? 'fw-bold text-theme' : 'text-muted'">
                {{ $t(step.title) }}
              </div>
            </div>
          </div>
        </div>
        <div class="d-block d-md-none mt-3">
          <div class="d-flex align-items-center mb-2">
            <span class="badge bg-theme rounded-pill me-2">{{ $t('Step') }} {{ currentStep }} / {{ steps.length }}</span>
            <h5 class="m-0 fw-bold text-dark">{{ $t(steps[currentStep - 1].title) }}</h5>
          </div>
          <div class="progress" style="height: 8px;">
            <div
              class="progress-bar bg-theme"
              role="progressbar"
              :style="{ width: (currentStep / steps.length) * 100 + '%' }"
            ></div>
          </div>
          <small class="text-muted mt-2 d-block">
            {{ $t('Next Steps') }}: {{ currentStep < steps.length ? $t(steps[currentStep].title) : $t('Finish') }}
          </small>
        </div>
      </div>
      <div
        class="form-card px-3 py-3 mb-3"
        v-show="currentStep == 1"
      >
        <div class="row">
          <div class="col-md-4 mb-3">
            <label
              class="form-label"
            >
              {{ $t('Select Branch') }}
              <span class="text-danger">*</span>
            </label>

            <div class="input-group-sm ">
              <select2
                v-model.trim="v$.form.branch_id.$model"
                @select="onSelectedBranch"
                :options="select2Branches.options"
                :settings="select2Branches.setting"
                :placeholder="$t('Select') + '...'"
              />
              <div v-for="error of v$.form.branch_id.$errors"
                class="d-block invalid-feedback"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
            </div>
          </div>
          <div class="col-md-8 mb-3">
            <label
              class="form-label"
            >
              {{ $t('Select Recipe') }}
              <span class="text-danger">*</span>
            </label>

            <div class="input-group-sm ">
              <select2
                v-model.trim="v$.form.inventory_recipe_id.$model"
                @select="onSelectedRecipe"
                :options="select2Recipes.options"
                :settings="select2Recipes.setting"
                :placeholder="$t('Select') + '...'"
              />
              <div v-for="error of v$.form.inventory_recipe_id.$errors"
                class="d-block invalid-feedback"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
            </div>
          </div>
          <div class="col-md-12">
            <span class="ms-1 text-secondary small">{{ $t('Specify Batch Size') }}</span>
            <div class="border rounded mb-3 mt-1">
              <div class="card-body">
                <label
                  for="multiplier"
                  class="form-label"
                >
                  {{ $t('Multiplier (x Standard Batch Size)') }}
                  <span class="text-danger">*</span>
                </label>
                <div class="input-group-sm">
                  <input
                    id="multiplier"
                    type="number"
                    inputmode="decimal"
                    v-model.trim="v$.form.batch_multiplier.$model"
                    class="form-control shadow-none"
                    :disabled="type === 'EDIT'"
                    :placeholder="$t('Enter multiplier') + '...'"
                  >
                  <div v-for="error of v$.form.batch_multiplier.$errors"
                    class="d-block invalid-feedback"
                    :key="error.$uid"
                  >
                    {{ error.$message }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          class="border-theme-subtle card"
          v-if="estimatedProduction"
        >
          <div class="bg-theme-subtle border-theme-subtle card-header">
            <i class="fa-solid fa-circle-info me-2" />
            {{ $t('Estimated Outcome') }}
          </div>
          <div class="card-body pb-0 row">
            <div class="col-md-6 pb-3">
              <div class="border rounded h-100 bg-white">
                <ul class="list-unstyled m-0 p-3">
                  <li>
                    <b>
                      {{ $t('Inputs required (Planned)') }}:
                    </b>
                    <ul
                      class="ps-3"
                      style="font-size:14px"
                    >
                      <li v-for="(input, index) in form.inputs">
                        {{ index+1 + '. ' + input.name + ': ' + formatNumberTwoDigit(input.planned_quantity) + ' ' + $t(input.unit_name) }}
                      </li>
                    </ul>
                  </li>
                </ul>
              </div>
            </div>
            <div class="col-md-6 pb-3">
              <div class="border rounded h-100 bg-white">
                <ul class="list-unstyled m-0 p-3">
                  <li>
                    <b>
                      {{ $t('Outputs to be produced') }}:
                    </b>
                    <ul
                      class="ps-3"
                      style="font-size:14px"
                    >
                      <li v-for="(output, index) in form.outputs">
                        {{ index+1 + '. ' + output.name + ': ' + formatNumberTwoDigit(output.planned_quantity) + ' ' + $t(output.unit_name) }}
                      </li>
                    </ul>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        class="form-card px-3 py-3 mb-3"
        v-if="currentStep == 2"
      >
        <template
          v-if="loadingStep2"
        >
          <placeholder-content :is-load="true" />
        </template>
        <template
          v-else
        >
          <h5
            class="mb-0"
          >
            <b>
              {{ $t('Review Ingredients & Cost Estimation') }}
            </b>
          </h5>
          <span
            style="font-size: 14px;"
          >
            {{ $t('Review the planned material usage, check stock availability at') }}
            <b>
              {{ form.branch_name }}
            </b>
            {{ $t(', and estimated costs based on current average costs.') }}
          </span>
          <div
            class="overflow-x-auto"
          >
            <table class="mt-3 table table-sm">
              <thead
                class="align-top table-light"
                style="
                  font-size: 14px;
                "
              >
                <tr>
                  <th
                    scope="col"
                    style="min-width: 200px;"
                  >
                    {{ $t('Inventory') }}
                  </th>
                  <th
                    scope="col"
                    style="min-width: 100px;"
                    class="text-end"
                  >
                    {{ $t('Planned Quantity') }}
                  </th>
                  <th
                    scope="col"
                    style="min-width: 50px;"
                  >
                    {{ $t('Unit') }}
                  </th>
                  <th
                    scope="col"
                    style="min-width: 100px;"
                    class="text-end"
                  >
                    {{ $t('Current Stock') }}
                  </th>
                  <th
                    scope="col"
                    style="min-width: 100px;"
                    class="text-end"
                  >
                    {{ $t('Est. Remaining') }}
                  </th>
                  <th
                    scope="col"
                    class="text-end"
                    style="min-width: 200px;"
                  >
                    {{ $t('Current Avg. Cost (THB/Unit)') }}
                  </th>
                  <th
                    scope="col"
                    class="text-end"
                    style="min-width: 200px;"
                  >
                    {{ $t('Est. Total Cost (THB)') }}
                  </th>
                </tr>
              </thead>
              <tbody
                style="
                  font-size: 14px;
                "
              >
                <tr
                  v-for="input in form.inputs"
                >
                  <td>
                    {{ $t(input.name) }}
                  </td>
                  <td
                    class="text-end"
                  >
                    {{ formatDigit(input.planned_quantity) }}
                  </td>
                  <td>
                    {{ $t(input.unit_name) }}
                  </td>
                  <td
                    class="text-end"
                  >
                    {{ formatDigit(input.current_stock) }}
                  </td>
                  <td
                    class="text-end"
                    :class="[input.current_stock < input.planned_quantity? 'text-danger': '']"
                  >
                    {{ formatDigit(input.current_stock - input.planned_quantity) }}
                  </td>
                  <td
                    class="text-end"
                  >
                    {{ formatDigit(input.planned_cost_per_unit) }}
                  </td>
                  <td
                    class="text-end"
                  >
                    {{ formatDigit(input.planned_total_cost) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            class="text-end"
          >
            <h6>
              {{ $t('Total Estimated Input Cost') + ': ' }}
              <b>
                {{ formatDigit(form.planned_total_input_cost) + ' ' + $t('THB') }}
              </b>
            </h6>
          </div>
        </template>
      </div>
      <div
        class="form-card px-3 py-3"
        v-if="currentStep == 3"
      >
        <h5
          class="mb-3"
        >
          <b>
            {{ $t('Review & Confirm Plan') }}
          </b>
        </h5>
        <b
          style="font-size: 16px;"
        >
          {{ $t('General Information') }}
        </b>
        <div class="row">
          <div class="col-md-4">
            {{ form.name }}
          </div>
          <div class="col-md-4">
            {{ $t('Batch Size') + ': ' + formatNumberTwoDigit(form.batch_multiplier) + $t('_BatchSize.x') }}
          </div>
          <div class="col-md-4">
            {{ $t('Branch') + ': ' + form.branch_name }}
          </div>
        </div>
        <div class="mt-3 row">
          <div class="col-md-12">
            <b>
              {{ $t('Planned Inputs Summary') }}
            </b>
            <div
              class="overflow-x-auto"
            >
              <table class="mt-1 table table-sm">
                <thead
                  class="align-top table-light"
                  style="
                    font-size: 14px;
                  "
                >
                  <tr>
                    <th
                      scope="col"
                      style="min-width: 150px;"
                    >
                      {{ $t('Inventory') }}
                    </th>
                    <th
                      scope="col"
                      style="min-width: 100px;"
                    >
                      {{ $t('Planned Quantity') }}
                    </th>
                    <th
                      scope="col"
                      class="text-end"
                      style="min-width: 100px;"
                    >
                      {{ $t('Current Stock') }}
                    </th>
                    <th
                      scope="col"
                      class="text-end"
                      style="min-width: 100px;"
                    >
                      {{ $t('Est. Remaining') }}
                    </th>
                  </tr>
                </thead>
                <tbody
                  style="
                    font-size: 14px;
                  "
                >
                  <tr
                    v-for="input in form.inputs"
                  >
                    <td>
                      {{ input.name }}
                    </td>
                    <td>
                      {{ formatDigit(input.planned_quantity) + ' ' + $t(input.unit_name) }}
                    </td>
                    <td
                      class="text-end"
                    >
                      {{ formatDigit(input.current_stock) }}
                    </td>
                    <td
                      class="text-end"
                      :class="[input.current_stock < input.planned_quantity? 'text-danger': '']"
                    >
                      {{ formatDigit(input.current_stock - input.planned_quantity) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="col-md-12">
            <b>
              {{ $t('Planned Outputs Summary') }}
            </b>
            <div
              class="overflow-x-auto"
            >
              <table class="mt-1 table table-sm">
                <thead
                  class="align-top table-light"
                  style="
                    font-size: 14px;
                  "
                >
                  <tr>
                    <th
                      scope="col"
                      style="min-width: 150px;"
                    >
                      {{ $t('Product / Inventory') }}
                    </th>
                    <th
                      scope="col"
                      style="min-width: 100px;"
                    >
                      {{ $t('Planned Quantity') }}
                    </th>
                    <th
                      scope="col"
                      style="min-width: 100px;"
                    >
                      {{ $t('Est. New Stock') }}
                    </th>
                    <th
                      scope="col"
                      class="text-end"
                      style="min-width: 100px;"
                    >
                      {{ $t('Est. Alloc. Cost (THB)') }}
                    </th>
                  </tr>
                </thead>
                <tbody
                  style="
                    font-size: 14px;
                  "
                >
                  <tr
                    v-for="output in form.outputs"
                  >
                    <td>
                      {{ output.name }}
                    </td>
                    <td>
                      {{ formatDigit(output.planned_quantity) + ' ' + $t(output.unit_name) }}
                    </td>
                    <td>
                      {{ formatDigit(output.current_stock + output.planned_quantity) + ' ' + $t(output.unit_name) }}
                    </td>
                    <td
                      class="text-end"
                    >
                      <b>
                        {{ formatDigit(output.planned_allocated_total_cost) }}
                      </b>
                      {{ '(' + $t('approx.') + output.cost_allocation_percent + '%)' }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div
          class="text-end"
        >
          {{ $t('Total Estimated Input Cost') + ': ' }}
          <b>
            {{
              formatNumberTwoDigit(form.planned_total_input_cost) + ' ' + $t('THB')
            }}
          </b>
        </div>
      </div>
    </div>
    <div
      class="bg-light border-top justify-content-center modal-footer py-2"
      v-if="!loading"
    >
      <ladda
        text="Previous"
        :btn-class="'btn btn-secondary btn-sm btn-block me-2'"
        icon="<i class='fas fa-arrow-circle-left'></i>"
        @click="prevStep"
        v-if="currentStep != 1"
      />
      <ladda
        text="Next"
        :btn-class="'btn btn-theme btn-sm btn-block me-2 '"
        icon="<i class='fas fa-arrow-circle-right'></i>"
        @click="nextStep"
        :disabled="v$.$invalid"
        v-if="currentStep < steps.length"
      />
      <ladda
        text="Save as Draft"
        v-if="(currentStep === steps.length && type === 'CREATE')"
        :btn-class="'btn btn-outline-theme btn-sm me-2 '"
        :loading="loadingSave"
        icon="<i class='fa-solid fa-compass-drafting'></i>"
        :disabled="v$.$invalid"
        @click="handleCreate(false)"
      />
      <ladda
        text="Submit Plan"
        v-if="(currentStep === steps.length && type === 'CREATE')"
        :btn-class="'btn btn-theme btn-sm btn-block me-2 '"
        :loading="loadingSave"
        icon="<i class='fa-solid fa-check'></i>"
        :disabled="v$.$invalid || !canSubmitPlan"
        @click="handleCreate(true)"
      />
      <ladda
        text="Save"
        v-if="type === 'EDIT'"
        :btn-class="'btn btn-theme btn-sm btn-block me-2'"
        :loading="loadingSave"
        icon="<i class='fa-solid fa-floppy-disk'></i>"
        :disabled="v$.$invalid"
        @click="handleEdit"
      />
      <button
        type="button"
        data-bs-dismiss="offcanvas"
        class="border-dark-subtle btn-sm btn btn-light"
      >
        {{ $t('Cancel') }}
      </button>
    </div>
  </div>
</template>

<script lang="js">
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import AccessDenied from "@/plugins/AccessDenied.vue";
import Ladda from '@/plugins/Ladda.vue';
import { useVuelidate } from '@vuelidate/core';
import validation from "./assets/template/validation";
import { InventoryProductionBatches } from '@/services/endpoint/inventory-production-batches.service';
import { AUTH, API_URLS } from '@/services/endpoint/_constants';
import Select2 from 'vue3-select2-component';
import signature  from '@/services/signature';
import numeral from 'numeral';

export default {
  setup: () => ({ v$: useVuelidate() }),
  validationConfig: {
    $lazy: true,
  },
  validations() {
    return validation(this.form)
  },
  props: {
    type: {
      type: String,
      default: 'CREATE'
    },
    id: {
      type: Number,
      default: 0
    },
  },
  data() {
    let vm = this;
    return {
      currentStep: 1,
      steps: [
        { id: 1, title: 'Select Branch, Recipe & Size' },
        { id: 2, title: 'Review Ingredients & Cost' },
        { id: 3, title: 'Summary' }
      ],
      selectInventory: [],
      listItems: [],
      loadingStep2: true,
      loading: true,
      hasPermission: false,
      loadingSave: false,
      select2Branches: {
        options: [],
        setting: {
          minimumResultsForSearch: Infinity,
        }
      },
      select2Recipes: {
        options: [],
        setting: {
          dropdownParent: '#OffcanvasForm',
          minimumResultsForSearch: true,
          multiple: false,
          language: {
            maximumSelected: function (e) {
              return vm.$t('You can only select') + ' ' + e.maximum + ' ' + vm.$t('items');
            },
            loadingMore: function (e) {
              return '<i class="fa-solid fa-spinner fa-spin" />'
            }
          },
          ajax: {
            beforeSend: function (request) {
              request.setRequestHeader('Authorization', 'Bearer ' + AUTH.TOKEN);
            },
            url: AUTH.URL + API_URLS.API_RAW_MATERIAL_PRODUCTION_RECECIPE,
            data: function (params) {
              var query = {
                search: params.term,
                page: params.page || 1,
                limit: 10
              };
              return signature($.param(query)).substring(1);
            },
            processResults: function (data, params) {
              let option = data.data;
              let pageCurrent = data.meta.current_page;
              option = option.map(function(item) {
                return {
                  id: item.id,
                  text: item.name,
                  inputs: item.inputs,
                  outputs: item.outputs
                }
              });
              return {
                results: option,
                pagination: {
                  more: (pageCurrent * 10) < data.meta.total
                }
              }
            }
          }
        }
      },
      selectedRecipe: {
        inventory_recipe_id: '',
        name: '',
        inputs: [],
        outputs: []
      },
      form: {
        branch_id: 1,
        branch_name: '',
        inventory_recipe_id: '',
        batch_multiplier: 1,
        name: '',
        planned_total_input_cost: '',
        note: '',
        inputs: [],
        outputs: []
      },
      optionsShelfLifeUnits: [],
      optionsExpiryAnchors: [],
      canSubmitPlan: true
    }
  },
  components: {
    PlaceholderContent,
    AccessDenied,
    Ladda,
    Select2
  },
  methods: {
    nextStep() {
      if (this.currentStep < this.steps.length) this.currentStep++;
      if (this.currentStep == 2) {
        this.setRecipeStock();
      }
    },
    prevStep() {
      if (this.currentStep > 1) this.currentStep--;
    },
    formatNumber(value) {
      return numeral(value).format("0,0");
    },
    formatDigit(value) {
      if ((value < 0.01) && value != 0) {
        return numeral(value).format("0,0.0000");
      }
      return numeral(value).format("0,0.00");
    },
    formatNumberTwoDigit(value) {
      return numeral(value).format("0,0.00");
    },
    formatNumberDigit(value) {
      return numeral(value).format("0,0.0000");
    },
    async onSelectedBranch(e) {
        this.form.branch_name = e.text;
    },
    async onSelectedRecipe(e) {
      this.form.name = e.text;
      this.selectedRecipe.name = e.text;
      this.selectedRecipe.inputs = e.inputs;
      this.selectedRecipe.outputs = e.outputs;
    },
    async setRecipeStock() {
      this.loadingStep2 = true;
      let vm = this;
      let params = {
        recipe_id: this.form.inventory_recipe_id,
        branch_id: this.form.branch_id
      };
      const queryString = new URLSearchParams(params).toString();
      await InventoryProductionBatches.getCheckRecipeStock(`?${queryString}`).then(res => {
        vm.form.inputs.forEach(input => {
          const match = res.data.data.find(item => item.inventory_id === input.inventory_id);
          input.current_stock = match? match.remaining_quantity: 0;
        });
        vm.form.outputs.forEach(output => {
          const match = res.data.data.find(item => item.inventory_id === output.inventory_id);
          output.current_stock = match? match.remaining_quantity: 0;
        });
        this.loadingStep2 = false;
      }).catch(err => {
			  throw err;
      });
    },
    async getCreateData() {
      await InventoryProductionBatches.getCreate().then(res => {
        let vm = this;
        this.select2Branches.options = res.data.data.branches.map(function(item) {
          return {
            id: item.id,
            text: vm.$t(item.name)
          }
        });
        this.form.branch_id = res.data.data.branches[0].id;
        this.form.branch_name = res.data.data.branches[0].name;
        this.hasPermission = true;
        this.loading = false;
      }).catch(err => {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
          this.loading = false;
        }
			  throw err;
      });
    },
    async getUpdateData() {
      await InventoryProductionBatches.getUpdate(this.id).then(res => {
        this.form = {
          name: res.data.data.name,
          inputs: res.data.data.inputs.map(function(input) {
            return {
              id: input.id,
              inventory_id: input.inventory_id,
              name: input.name,
              unit_id: input.unit_id,
              unit_name: input.unit_name,
              quantity: input.quantity
            }
          }),
          outputs: res.data.data.outputs.map(function(output) {
            return {
              id: output.id,
              inventory_id: output.inventory_id,
              name: output.name,
              unit_id: output.unit_id,
              unit_name: output.unit_name,
              quantity: output.quantity,
              shelf_life_value: output.shelf_life_value,
              shelf_life_unit: output.shelf_life_unit,
              cost_allocation_percent: output.cost_allocation_percent
            }
          }),
          description: res.data.data.description,
          is_active: res.data.data.active
        };
        this.hasPermission = true;
        this.loading = false;
      }).catch(err => {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
          this.loading = false;
        }
			  throw err;
      });
    },
    setParamsBeforeSendCreate() {
      let params = _.cloneDeep(this.form);
      delete params.branch_name;
      return $.each(params, (key, value) => {
        if (value === "" || value.length === 0) {
          delete params[key];
        }
      });
    },
    setParamsBeforeSendUpdate() {
      let params = _.clone(this.form);
      delete params.cost_allocation;
      return $.each(params, (key, value) => {
        if (value === "" || value.length === 0) {
          delete params[key];
        }
      });
    },
    async handleEdit() {
      this.loadingSave = true;
      this.v$.$touch();
      if(this.v$.$invalid) {
        this.loadingSave = false;
        return;
      }
      await InventoryProductionBatches.update(this.id, this.setParamsBeforeSendUpdate()).then(res => {
        this.$parent.fetchData();
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('Update Recipe!'));
        $(this.$refs.offcanvas).offcanvas('hide');
        this.loadingSave = false;
      }).catch(err => {
        if (err?.response?.data?.errors) {
          this.errors = err?.response?.data?.errors;
        }
        this.loadingSave = false;
      });
    },
    async handleCreate(confirm = false) {
      this.loadingSave = true;
      this.v$.$touch();
      if(this.v$.$invalid) {
        this.loadingSave = false;
        return;
      }
      if(confirm && !this.canSubmitPlan) {
        this.loadingSave = false;
        return;
      }
      this.form.confirm = confirm;
      await InventoryProductionBatches.create(this.setParamsBeforeSendCreate()).then(res => {
        this.$parent.fetchData();
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('Save Recipe!'));
        $(this.$refs.offcanvas).offcanvas('hide');
        this.loadingSave = false;
      }).catch(err => {
        if (err?.response?.data?.errors) {
          this.errors = err?.response?.data?.errors;
        }
        this.loadingSave = false;
      });
    },
  },
  async mounted() {
    $(this.$refs.offcanvas).offcanvas('show');

    $(this.$refs.offcanvas).on('shown.bs.offcanvas',async () => {
      this.loading = true;
      try {
        if (this.type === 'CREATE') await this.getCreateData();
        if (this.type === 'EDIT') await this.getUpdateData();
        this.loading = false;
      } catch (err) {
      }
    });

    $(this.$refs.offcanvas).on('hidden.bs.offcanvas', () => {
      this.$parent.offcanvasFroms.open = false;
    });
  },
  computed: {
    estimatedProduction() {
      if (this.selectedRecipe.inputs.length < 1 ||this.selectedRecipe.outputs.length < 1) return false;
      let vm = this;
      let batchMultiplier = vm.form.batch_multiplier? vm.form.batch_multiplier: 0;
      this.canSubmitPlan = true;
      this.form.inputs = this.selectedRecipe.inputs.map(function(input) {
        let quantity = input.quantity*batchMultiplier;
        let costPerUnit = input.cost;
        if (input.current_stock < quantity) {
          vm.canSubmitPlan = false;
        }
        return {
          inventory_id: input.inventory_id,
          name: input.name,
          planned_quantity: quantity,
          planned_cost_per_unit: costPerUnit,
          planned_total_cost: quantity*costPerUnit,
          current_stock: input.current_stock,
          unit_id: input.unit_id,
          unit_name: input.unit_name
        }
      });
      this.form.planned_total_input_cost = (this.form.inputs.reduce((sum, value) => {
        return sum + (value.planned_total_cost*1);
      }, 0)).toFixed(4);
      this.form.outputs = this.selectedRecipe.outputs.map(function(output) {
        let quantity = output.quantity*batchMultiplier;
        let totalCost = (vm.form.planned_total_input_cost*(output.cost_allocation_percent/100)).toFixed(4);
        return {
          inventory_id: output.inventory_id,
          name: output.name,
          planned_quantity: quantity,
          planned_cost_per_unit: totalCost/quantity,
          planned_allocated_total_cost: totalCost,
          cost_allocation_percent: output.cost_allocation_percent,
          unit_id: output.unit_id,
          unit_name: output.unit_name,
          applied_shelf_life_value: output.shelf_life_value,
          applied_shelf_life_unit: output.shelf_life_unit,
          cost_allocation_percent: output.cost_allocation_percent
        }
      });
      return true;
    },
    progressPercentage() {
      return ((this.currentStep) / (this.steps.length)) * 100;
    }
  }
}
</script>
